import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, MicOff, Send, Sparkles, Volume2, X, Loader2, Brain, Square, ChevronRight, Package, LineChart, Users, Puzzle } from "lucide-react";
import api, { extractErr } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import "../pages/merchant/aiStoreManager.css";

export const ASM_TALK_EVENT = "merastartup:asm-talk";
export const ASM_STORE_CHANGED_EVENT = "merastartup:asm-store-changed";

const STORE_CHANGE_TOOLS = new Set([
  "create_product", "update_product", "set_product_variants", "upsert_page",
  "create_category", "create_coupon", "update_order_status", "generate_image",
  "set_store_seo", "update_store_info",
]);

const SUGGESTS = [
  { text: "Add a new product to my store", focus: "products" },
  { text: "Improve my SEO for better traffic", focus: null },
  { text: "Write About Us and Contact pages", focus: null },
  { text: "Add size variants to my latest product", focus: "products" },
];

function speechRecognitionCtor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function pickVoice(lang) {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const exact = voices.find((v) => v.lang === lang);
  if (exact) return exact;
  const prefix = (lang || "").split("-")[0];
  return voices.find((v) => (v.lang || "").toLowerCase().startsWith(prefix)) || voices.find((v) => (v.lang || "").startsWith("en")) || null;
}

function AsmBot({ className }) {
  return (
    <div className={`asm-bot-wrap ${className || ""}`} aria-hidden="true">
      <svg viewBox="0 0 160 160">
        <defs>
          <linearGradient id="asmBotBody" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#e0e7ff" />
            <stop offset="55%" stopColor="#a5b4fc" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>
        <ellipse cx="80" cy="148" rx="38" ry="6" fill="rgba(76,29,149,0.35)" />
        <rect x="72" y="22" width="16" height="18" rx="8" fill="#c4b5fd" />
        <circle cx="80" cy="18" r="7" fill="#22d3ee" />
        <rect x="28" y="42" width="104" height="92" rx="36" fill="url(#asmBotBody)" />
        <rect x="40" y="62" width="80" height="42" rx="20" fill="#0f172a" />
        <circle cx="62" cy="83" r="10" fill="#67e8f9" />
        <circle cx="98" cy="83" r="10" fill="#67e8f9" />
        <path d="M70 108c6 8 14 8 20 0" stroke="#312e81" strokeWidth="4" fill="none" strokeLinecap="round" />
        <circle cx="22" cy="88" r="10" fill="#818cf8" />
        <circle cx="138" cy="88" r="10" fill="#818cf8" />
        <rect x="68" y="118" width="24" height="14" rx="4" fill="#4f46e5" />
      </svg>
    </div>
  );
}

export default function AIStoreManagerDock({ status: initial }) {
  const { tenant } = useMerchantAuth();
  const [status, setStatus] = useState(initial);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [workingLabel, setWorkingLabel] = useState("");
  const [voiceLang, setVoiceLang] = useState(initial?.voice_lang || "hi-IN");
  const scrollRef = useRef(null);
  const recRef = useRef(null);
  const focusRef = useRef(null);
  const sendTextRef = useRef(async () => {});
  const startListeningRef = useRef(() => {});
  const abortRef = useRef(null);

  const storeName = status?.store_name || tenant?.name || "this store";

  useEffect(() => {
    setStatus(initial);
    if (initial?.voice_lang) setVoiceLang(initial.voice_lang);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchant/ai-store-manager/history").then(({ data }) => {
      if (cancelled) return;
      const rows = (data.messages || []).map((m) => ({ role: m.role, text: m.text || m.content || "" })).filter((m) => m.text);
      if (rows.length) setMessages(rows);
      setHydrated(true);
    }).catch(() => {
      if (!cancelled) setHydrated(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hydrated || messages.length) return;
    setMessages([{
      role: "assistant",
      welcome: true,
      text: status?.has_api_key
        ? `Hi! I'm Merastartup AI. I can help ${storeName} with products, variants, pages, SEO, customers, coupons, orders, images, and addons you already bought.`
        : "Add your Grok (xAI) API key in App Store → AI Store Manager, then tap Learn about my store.",
    }]);
  }, [status, storeName, messages.length, hydrated]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy, activities]);

  const stopTalking = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* ignore */ }
      abortRef.current = null;
    }
  };

  const speak = (text) => {
    if (!status?.voice_enabled || !text || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = voiceLang || "hi-IN";
      const voice = pickVoice(u.lang);
      if (voice) u.voice = voice;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    } catch {
      setSpeaking(false);
    }
  };

  const sendText = async (text, nextFocus) => {
    const msg = (text || "").trim();
    if (!msg || busy) return;
    if (nextFocus) focusRef.current = nextFocus;
    setInput("");
    stopTalking();
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    setWorkingLabel("Orchestrating your store");
    setActivities([{ name: "think", label: "Analyzing store settings", status: "working" }]);
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = controller;
    try {
      const { data } = await api.post(
        "/merchant/ai-store-manager/chat",
        { message: msg, language: voiceLang, focus: focusRef.current || undefined },
        { timeout: 90000, signal: controller?.signal },
      );
      const acts = data.activities || [];
      setActivities(acts.length ? acts : [{ name: "done", label: "Ready", status: "ok" }]);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, activities: acts }]);
      speak(data.reply);
      if (acts.some((a) => STORE_CHANGE_TOOLS.has(a.name))) {
        window.dispatchEvent(new CustomEvent(ASM_STORE_CHANGED_EVENT));
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError") {
        setActivities([{ name: "stop", label: "Stopped", status: "ok" }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", text: extractErr(err) || "I could not complete that." }]);
        setActivities([]);
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
      setWorkingLabel("");
    }
  };
  sendTextRef.current = sendText;

  const learn = async () => {
    setBusy(true);
    setWorkingLabel("Learning this store");
    setActivities([{ name: "learn", label: "Scanning products & categories", status: "working" }]);
    try {
      const { data } = await api.post("/merchant/ai-store-manager/learn", {}, { timeout: 60000 });
      setStatus((s) => ({ ...s, learned_at: data.learned_at, knowledge_summary: data.summary }));
      setActivities([{ name: "learn", label: data.summary || "Store memory ready", status: "ok" }]);
      setMessages((m) => [...m, { role: "assistant", text: `I have learned this store. ${data.summary || ""} I am ready to work.` }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: extractErr(err) || "Learn failed." }]);
      setActivities([]);
    } finally {
      setBusy(false);
      setWorkingLabel("");
    }
  };

  const startListening = () => {
    if (busy) return;
    stopTalking();
    const Ctor = speechRecognitionCtor();
    if (!Ctor) {
      setMessages((m) => [...m, { role: "assistant", text: "Voice needs Chrome or Edge. You can still type in any Indian language." }]);
      return;
    }
    try {
      try { recRef.current?.stop(); } catch { /* ignore */ }
      const rec = new Ctor();
      rec.lang = voiceLang || "hi-IN";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e) => {
        const said = e.results?.[0]?.[0]?.transcript || "";
        if (said) sendTextRef.current(said);
      };
      rec.onerror = () => {
        setListening(false);
        setMessages((m) => [...m, { role: "assistant", text: "Could not hear that. Try again or type in your language." }]);
      };
      rec.onend = () => setListening(false);
      recRef.current = rec;
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
      setMessages((m) => [...m, { role: "assistant", text: "Microphone permission is required to talk." }]);
    }
  };
  startListeningRef.current = startListening;

  const toggleMic = () => {
    if (listening) {
      try { recRef.current?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    startListening();
  };

  useEffect(() => {
    const onTalk = (e) => {
      const focus = e?.detail?.focus || "products";
      focusRef.current = focus;
      setOpen(true);
      setMessages((m) => {
        if (m.some((x) => x.hint === "talk-products")) return m;
        return [...m, {
          role: "assistant",
          hint: "talk-products",
          text: "I'm listening. Tell me the product name, price, stock, and any size or color variants — I will save it on this store.",
        }];
      });
      window.setTimeout(() => startListeningRef.current(), 400);
    };
    window.addEventListener(ASM_TALK_EVENT, onTalk);
    return () => window.removeEventListener(ASM_TALK_EVENT, onTalk);
  }, []);

  const locked = !status?.has_api_key;
  const langs = status?.languages || [];
  const conversation = messages.filter((m) => !m.welcome);
  const showWelcome = open && conversation.length === 0;
  const doneCount = activities.filter((a) => a.status === "ok").length;
  const pct = busy ? Math.min(92, 18 + doneCount * 22 + (activities.some((a) => a.status === "working") ? 10 : 0)) : 100;

  return (
    <>
      {busy && (
        <div className="asm-work-overlay" data-testid="asm-working">
          <div className="asm-work-card">
            <div className="asm-work-top">
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-violet-200">Merastartup AI</div>
                <div className="asm-work-copy">
                  <h3>Orchestrating<br />your <span>store</span></h3>
                  <p>{workingLabel === "Learning this store" ? "Indexing catalog, SEO, customers and addons…" : "AI is analyzing your store, products, customers and settings to give you the best assistance…"}</p>
                </div>
              </div>
              <AsmBot className="asm-work-bot" />
            </div>
            <div className="asm-progress" data-testid="asm-progress"><i style={{ width: `${pct}%` }} /></div>
            <div className="asm-work-grid">
              <div className="asm-work-list">
                {(activities.length ? activities : [{ label: workingLabel, status: "working" }]).map((a, i) => (
                  <div key={i} className={`asm-work-row ${a.status || "working"}`}>
                    {a.status === "working" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="asm-dot" />}
                    {a.detail || a.label}
                  </div>
                ))}
              </div>
              <div className="asm-tip">
                <div className="font-bold text-violet-100 mb-1">Did you know?</div>
                You can ask me to add products with variants, write About & Contact pages, improve SEO, or manage coupons and orders.
              </div>
            </div>
            <div className="asm-work-actions">
              <button type="button" data-testid="asm-stop-talk" className="asm-icon-btn stop" onClick={stopTalking}>
                <Square className="w-3.5 h-3.5" /> Stop talking
              </button>
            </div>
          </div>
        </div>
      )}

      {!open && (
        <button type="button" onClick={() => setOpen(true)} data-testid="asm-fab" className="asm-fab" aria-label="AI Store Manager">
          <span className="asm-fab-bubble">Need help?<br />Ask AI Store Manager!</span>
          <AsmBot className="asm-fab-bot" />
          <span className="asm-fab-cta">AI Store Manager <ChevronRight className="w-3.5 h-3.5" /></span>
        </button>
      )}

      {open && (
        <div data-testid="asm-panel" className="asm-panel">
          <div className="asm-panel-head">
            <div className="asm-mark"><Sparkles className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-white leading-tight" data-testid="asm-title">Merastartup AI</div>
              <div className="text-[11px] text-violet-200/90 truncate">{storeName}</div>
            </div>
            <span className="asm-online"><i /> Online</span>
            {speaking ? (
              <button type="button" onClick={stopTalking} data-testid="asm-stop-talk-panel" className="asm-icon-btn stop" aria-label="Stop talking">
                <Square className="w-3.5 h-3.5" />
              </button>
            ) : null}
            <button type="button" onClick={() => { focusRef.current = null; stopTalking(); setOpen(false); }} data-testid="asm-close" className="asm-icon-btn"><X className="w-5 h-5" /></button>
          </div>

          {showWelcome ? (
            <>
              <div className="asm-hero">
                <div className="asm-hero-row">
                  <div>
                    <h2>Hi! I&apos;m Merastartup AI</h2>
                    <p>Your AI assistant for {storeName}. I can help with products, variants, pages, SEO, customers, images, addons and more.</p>
                  </div>
                  <AsmBot className="asm-hero-bot" />
                </div>
              </div>
              <div className="asm-tiles">
                <button type="button" className="asm-tile products" onClick={() => sendText("Help me add or edit products and variants", "products")}><Package className="w-4 h-4" /> Products</button>
                <button type="button" className="asm-tile seo" onClick={() => sendText("Improve my store SEO")}><LineChart className="w-4 h-4" /> SEO</button>
                <button type="button" className="asm-tile customers" onClick={() => sendText("Show my recent customers")}><Users className="w-4 h-4" /> Customers</button>
                <button type="button" className="asm-tile addons" onClick={() => sendText("Which purchased addons can I turn on or off?")}><Puzzle className="w-4 h-4" /> Addons</button>
              </div>
              <div className="asm-ask">Try asking me:</div>
              {SUGGESTS.map((s) => (
                <button key={s.text} type="button" className="asm-chip" disabled={busy || locked} onClick={() => sendText(s.text, s.focus)}>
                  {s.text} <ChevronRight className="w-4 h-4 opacity-60" />
                </button>
              ))}
            </>
          ) : (
            <div ref={scrollRef} className="asm-thread">
              {messages.map((m, i) => (
                <div key={i} className={`asm-bubble ${m.role}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
                  {m.activities?.length > 0 && (
                    <ul className="asm-acts">
                      {m.activities.map((a, j) => (
                        <li key={j}>{a.status === "ok" ? "●" : "○"} {a.detail || a.label}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="asm-foot">
            <div className="flex items-center justify-between gap-2">
              <button type="button" disabled={busy || locked} onClick={learn} data-testid="asm-learn-dock" className="asm-learn">
                <Brain className="w-3.5 h-3.5" /> Learn about my store
              </button>
              {langs.length > 0 ? (
                <select
                  data-testid="asm-dock-lang"
                  className="asm-lang"
                  value={voiceLang}
                  onChange={(e) => setVoiceLang(e.target.value)}
                  aria-label="Talk in"
                >
                  {langs.map((l) => (
                    <option key={l.code} value={l.code}>{l.native}</option>
                  ))}
                </select>
              ) : null}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); sendText(input); }} className="asm-composer">
              {status?.voice_enabled ? (
                <button type="button" disabled={busy || locked} onClick={toggleMic} data-testid="asm-mic" className={`asm-icon-btn solid ${listening ? "live" : ""}`}>
                  {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              ) : (
                <span className="asm-icon-btn solid opacity-40"><Volume2 className="w-4 h-4" /></span>
              )}
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy || locked}
                data-testid="asm-input"
                placeholder={locked ? "Add your Grok key first" : (listening ? "Listening…" : "Ask anything about your store…")}
                className="asm-input"
              />
              {speaking ? (
                <button type="button" onClick={stopTalking} data-testid="asm-stop-talk" className="asm-icon-btn stop" aria-label="Stop talking">
                  <Square className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button type="submit" disabled={busy || locked || !input.trim()} data-testid="asm-send" className="asm-icon-btn send">
                  <Send className="w-4 h-4" />
                </button>
              )}
            </form>
            <div className="asm-fine">
              Powered by Merastartup AI · {storeName} only · <Link to="/admin/addons/ai-store-manager" className="underline">settings</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
